/**
 * ADLAmendmentModal.jsx
 * CCH Care — Phase 3
 * Last Modified: 2026-05-19
 *
 * HIPAA-compliant amendment flow for signed ADL sections.
 * Steps:
 *   1. Row selection (for multi-row sections)
 *   2. Field selection + old/new value entry
 *   3. Reason entry (required)
 *   4. Confirm
 *
 * Changes (2026-05-19) — single-row section fix:
 * - The `rows` memo previously did `Object.values(sectionData)` on any
 *   non-array sectionData. For the two single-row sections (bowel,
 *   urination) records.php returns ONE row object, so Object.values()
 *   shredded it into an array of its column values — rows[0] became a
 *   scalar id, every field read "(empty)", and Submit stayed disabled.
 *   The memo now detects a single row object by its pk field and wraps
 *   it as [obj]; Object.values() is kept only for a genuine keyed map.
 *   (ADLFlowSheetPage's amendmentSectionData was fixed in parallel —
 *   this guard is defense-in-depth so the modal is correct regardless
 *   of what shape it's handed.)
 *
 * Changes (2026-05-16) — Late Documentation Phase 3:
 * - ALLOWED_FIELDS synced to the backend allowlist. skin_checks: dropped
 *   'notes' (not a real column, rejected by the backend — a 422) and
 *   added the real 'wound_care_notes'. meals: added 'diet_type',
 *   'fluid_intake_ml', 'supplement' so all seven backend-amendable meal
 *   fields are reachable. ALLOWED_FIELDS now matches
 *   adl_amendment_allowed_fields() in signatures.php.
 * - Constrained fields are now type 'select', not free text/number:
 *   meals diet_type/assist_level/percent_consumed, repositioning
 *   position/assist_level, fluid_intake fluid_type, bowel consistency,
 *   urination output_type/urine_color, skin_checks result. The amend
 *   flow could previously write an off-list value into a picklist field.
 *   Option sets mirror the parent ADL forms; FLUID_TYPES and URINE_TYPES
 *   are imported from config/constants, the remaining maps are declared
 *   here (a follow-up should promote the form-local sets to constants
 *   so the forms and this modal share one source).
 * - getOldValue() is now field-type-aware: it no longer treats a numeric
 *   0 or 1 as a boolean (which mis-rendered e.g. percent_consumed 0 as
 *   "No"). getDisplayValue() added so the "Current Value" display shows
 *   the option label rather than the raw stored code.
 *
 * Changes (2026-04-22) — Alpha-concat cleanup:
 * - HIPAA notice border: `1px solid ${COLORS.warning}44` →
 *   `1px solid ${COLORS.warningAlpha}`. Template-literal concat was
 *   producing invalid CSS ("var(--color-warning)44"); the old
 *   rendering relied on the browser ignoring malformed values.
 * - Reason textarea error border: `COLORS.danger + '88'` →
 *   `COLORS.dangerAlpha`. Note: dangerAlpha is ~27% alpha vs the old
 *   ~53%, so the red hint is lighter now. Paired with the existing
 *   "A reason is required" text below, the softer border is cleaner.
 * - Error banner border: hardcoded `rgba(231,76,60,0.2)` →
 *   `COLORS.dangerAlpha`. The hardcode also pinned to light-mode
 *   danger hue — border now themes correctly in dark mode.
 *
 * ESLint fixes (prior):
 * - allowedFields wrapped in useMemo
 * - rows wrapped in useMemo
 * - backdrop div: role/tabIndex/onKeyDown
 * - three labels: htmlFor + matching id on inputs
 */

import { useState, useEffect, useMemo } from 'react';
import { COLORS, FLUID_TYPES, URINE_TYPES, ASSIST_LEVELS } from '../../config/constants';

import { formatTime } from '../../utils/formatters';

// ── Picklist option sets ──────────────────────────────────────
// These mirror the constrained choices in the parent ADL forms so an
// amendment can't introduce an off-list value. FLUID_TYPES / URINE_TYPES
// come from config/constants; the maps below currently live local to the
// form files — a follow-up should promote them to config/constants so
// the forms and this modal share a single source.
const POSITION_LABELS = {
  left: 'Left Side', right: 'Right Side', back: 'Supine',
  elevated: 'Elevated HOB', chair: 'Chair/OOB',
};
const REPO_ASSIST_LABELS = {
  independent: 'Independent', '1_person': '1 Person',
  '2_person': '2 Person', mechanical_lift: 'Mech. Lift', refused: 'Refused',
};
const MEAL_ASSIST_LEVELS = ['independent', 'setup', 'minimal', 'moderate', 'total', 'refused'];
const DIET_PRESETS       = ['Regular', 'Mechanical Soft', 'Pureed', 'Liquid', 'NPO'];
const CONSISTENCY_OPTS   = ['Normal', 'Hard', 'Soft', 'Loose', 'Liquid'];
const URINE_COLOR_OPTS   = ['Clear', 'Yellow', 'Dark Yellow', 'Amber', 'Orange', 'Red', 'Cloudy'];
const PERCENT_OPTS       = [0, 25, 50, 75, 100];

const opt = (value, label) => ({ value: String(value), label });

const PICKLIST_OPTIONS = {
  position:        Object.entries(POSITION_LABELS).map(([v, l]) => opt(v, l)),
  repoAssist:      Object.entries(REPO_ASSIST_LABELS).map(([v, l]) => opt(v, l)),
  mealAssist:      MEAL_ASSIST_LEVELS.map(v => opt(v, v.charAt(0).toUpperCase() + v.slice(1))),
  dietType:        DIET_PRESETS.map(v => opt(v, v)),
  consistency:     CONSISTENCY_OPTS.map(v => opt(v.toLowerCase(), v)),
  urineColor:      URINE_COLOR_OPTS.map(v => opt(v.toLowerCase().replace(' ', '_'), v)),
  percentConsumed: PERCENT_OPTS.map(v => opt(v, `${v}%`)),
  fluidType:       FLUID_TYPES.map(v => opt(v, v)),
  outputType:      URINE_TYPES.map(u => opt(u.code, u.label)),
  result:          [opt('pass', 'Pass'), opt('fail', 'Fail / Issue')],
};

// ── Allowed fields per section ────────────────────────────────
const ALLOWED_FIELDS = {
  hygiene: [
    { key: 'assistance_level', label: 'Assistance Level', type: 'select',
      options: ASSIST_LEVELS.map(({ code, label }) => ({ value: code, label })) },
    { key: 'notes', label: 'Notes', type: 'text' },
  ],
  meals: [
    { key: 'percent_consumed', label: '% Consumed',          type: 'select',  options: PICKLIST_OPTIONS.percentConsumed },
    { key: 'diet_type',        label: 'Diet Type',           type: 'select',  options: PICKLIST_OPTIONS.dietType        },
    { key: 'assist_level',     label: 'Assist Level',        type: 'select',  options: PICKLIST_OPTIONS.mealAssist      },
    { key: 'fluid_intake_ml',  label: 'Fluids at Meal (mL)', type: 'number'  },
    { key: 'supplement',       label: 'Supplement',          type: 'text'    },
    { key: 'refused',          label: 'Refused',             type: 'boolean' },
    { key: 'notes',            label: 'Notes',               type: 'text'    },
  ],
  repositioning: [
    { key: 'position',     label: 'Position',     type: 'select',  options: PICKLIST_OPTIONS.position  },
    { key: 'assist_level', label: 'Assist Level', type: 'select',  options: PICKLIST_OPTIONS.repoAssist },
    { key: 'was_refused',  label: 'Refused',      type: 'boolean' },
    { key: 'skin_intact',  label: 'Skin Intact',  type: 'boolean' },
    { key: 'notes',        label: 'Notes',        type: 'text'    },
  ],
  fluid_intake: [
    { key: 'fluid_type', label: 'Fluid Type',  type: 'select',  options: PICKLIST_OPTIONS.fluidType },
    { key: 'amount_ml',  label: 'Amount (ml)', type: 'number'  },
    { key: 'refused',    label: 'Refused',     type: 'boolean' },
    { key: 'notes',      label: 'Notes',       type: 'text'    },
  ],
  bowel: [
    { key: 'had_bm',      label: 'Had BM',      type: 'boolean' },
    { key: 'consistency', label: 'Consistency', type: 'select',  options: PICKLIST_OPTIONS.consistency },
    { key: 'incontinent', label: 'Incontinent', type: 'boolean' },
    { key: 'notes',       label: 'Notes',       type: 'text'    },
  ],
  urination: [
    { key: 'output_type',          label: 'Output Type',      type: 'select',  options: PICKLIST_OPTIONS.outputType },
    { key: 'incontinent_episodes', label: 'Incont. Episodes', type: 'number'  },
    { key: 'urine_color',          label: 'Urine Color',      type: 'select',  options: PICKLIST_OPTIONS.urineColor },
    { key: 'notes',                label: 'Notes',            type: 'text'    },
  ],
  skin_checks: [
    { key: 'result',               label: 'Result',           type: 'select',  options: PICKLIST_OPTIONS.result },
    { key: 'body_location',        label: 'Body Location',    type: 'text'    },
    { key: 'description',          label: 'Description',      type: 'text'    },
    { key: 'wound_care_performed', label: 'Wound Care Done',  type: 'boolean' },
    { key: 'wound_care_notes',     label: 'Wound Care Notes', type: 'text'    },
    { key: 'nurse_notified',       label: 'Nurse Notified',   type: 'boolean' },
  ],
  safety_checks: [
    { key: 'is_checked', label: 'Checked', type: 'boolean' },
    { key: 'notes',      label: 'Notes',   type: 'text'    },
  ],
};

const SECTION_CONFIG = {
  hygiene:       { label: 'Hygiene',       icon: '🛁', pkField: 'adl_hygiene_id',       rowLabel: r => r.task_type?.replace(/_/g,' ') ?? 'Task'                            },
  meals:         { label: 'Meals',         icon: '🍽️', pkField: 'adl_meal_id',          rowLabel: r => r.meal_type?.replace(/_/g,' ') ?? 'Meal'                            },
  repositioning: { label: 'Repositioning', icon: '🔄', pkField: 'adl_repo_id',          rowLabel: r => r.position ?? formatTime(r.performed_at) ?? 'Entry'                 },
  fluid_intake:  { label: 'Fluid Intake',  icon: '💧', pkField: 'adl_fluid_id',         rowLabel: r => `${r.fluid_type ?? 'Fluid'} ${r.amount_ml ? r.amount_ml+'ml' : ''}`.trim() },
  bowel:         { label: 'Bowel',         icon: '📋', pkField: 'adl_bowel_id',         rowLabel: () => 'Bowel Record'                                                     },
  urination:     { label: 'Urination',     icon: '🚽', pkField: 'adl_urination_id',     rowLabel: () => 'Urination Record'                                                 },
  skin_checks:   { label: 'Skin Checks',   icon: '👁️', pkField: 'adl_skin_check_id',   rowLabel: r => r.body_location ?? formatTime(r.performed_at) ?? 'Entry'            },
  safety_checks: { label: 'Safety Checks', icon: '✅', pkField: 'adl_safety_check_id',  rowLabel: r => r.item_label ?? r.checklist_item_id ?? 'Item'                       },
};

const SINGLE_ROW_SECTIONS = new Set(['bowel', 'urination']);

function getOldValue(row, field) {
  if (!row || !field || row[field.key] === undefined) return '';
  const val = row[field.key];
  if (val === null) return '';
  if (field.type === 'boolean') return val ? 'Yes' : 'No';
  return String(val);
}

// Display form of a stored value — for select fields, resolve the raw
// stored code to its human-readable option label.
function getDisplayValue(row, field) {
  const raw = getOldValue(row, field);
  if (field?.type === 'select' && field.options) {
    return field.options.find(o => o.value === raw)?.label ?? raw;
  }
  return raw;
}

export default function ADLAmendmentModal({
  isOpen,
  section,
  sectionData = [],
  amending = false,
  error = null,
  onConfirm,
  onCancel,
}) {
  const [step, setStep]                   = useState('row');
  const [selectedRow, setSelectedRow]     = useState(null);
  const [selectedField, setSelectedField] = useState(null);
  const [newValue, setNewValue]           = useState('');
  const [reason, setReason]               = useState('');

  const cfg = SECTION_CONFIG[section] ?? { label: section, icon: '📋', pkField: 'id', rowLabel: () => 'Entry' };

  const allowedFields = useMemo(
    () => ALLOWED_FIELDS[section] ?? [],
    [section]
  );

  // sectionData is normally an array of row objects. The two single-row
  // sections (bowel, urination) come from records.php as ONE row object,
  // not an array — identify that case by the section's pk field and wrap
  // it as [obj]. Object.values() on a row object is wrong: it would
  // shred the row into an array of its column values, so rows[0] would
  // be a scalar (an id) instead of the row. The page already normalises
  // this, but the guard keeps the modal correct no matter what it's
  // handed. The Object.values() branch remains only for a genuine keyed
  // map of rows ({id: {...}, id: {...}}).
  const rows = useMemo(() => {
    if (Array.isArray(sectionData)) return sectionData;
    if (!sectionData || typeof sectionData !== 'object') return [];
    if (cfg.pkField in sectionData) return [sectionData]; // single row object
    return Object.values(sectionData);                    // keyed map of rows
  }, [sectionData, cfg.pkField]);

  useEffect(() => {
    if (!isOpen) return;
    setNewValue('');
    setReason('');
    setSelectedField(null);

    if (SINGLE_ROW_SECTIONS.has(section) || rows.length === 1) {
      setSelectedRow(rows[0] ?? null);
      setStep('field');
    } else {
      setSelectedRow(null);
      setStep('row');
    }
  }, [isOpen, section, rows]);

  const fieldDef = useMemo(() =>
    allowedFields.find(f => f.key === selectedField?.key),
  [allowedFields, selectedField]);

  const oldValue = useMemo(() =>
    selectedRow && selectedField ? getDisplayValue(selectedRow, selectedField) : '',
  [selectedRow, selectedField]);

  function handleFieldSelect(field) {
    setSelectedField(field);
    setNewValue(getOldValue(selectedRow, field));
    setStep('reason');
  }

  function handleBack() {
    if (step === 'reason') { setStep('field'); setNewValue(''); }
    else if (step === 'field' && !SINGLE_ROW_SECTIONS.has(section) && rows.length > 1) { setStep('row'); }
    else onCancel?.();
  }

  function handleSubmit() {
    if (!reason.trim()) return;
    const rowId = selectedRow?.[cfg.pkField] ?? selectedRow?.id ?? null;
    onConfirm?.({
      section,
      sectionRowId: rowId,
      fieldName:    selectedField?.key,
      oldValue,
      newValue,
      reason: reason.trim(),
    });
  }

  if (!isOpen || !section) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        role="button"
        tabIndex={0}
        onClick={onCancel}
        onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && onCancel()}
        style={{
          position: 'fixed', inset: 0,
          background: 'rgba(0,0,0,0.5)', zIndex: 1000,
        }}
      />

      {/* Sheet */}
      <div style={{
        position: 'fixed', bottom: 0, left: 0, right: 0,
        background: COLORS.surface, borderRadius: '16px 16px 0 0',
        padding: '20px 20px 36px', zIndex: 1001,
        maxWidth: 480, margin: '0 auto',
        maxHeight: '85vh', overflowY: 'auto',
      }}>
        {/* Handle */}
        <div style={{
          width: 36, height: 4, background: COLORS.border,
          borderRadius: 999, margin: '0 auto 16px',
        }} />

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
          <button onClick={handleBack} style={{
            background: 'none', border: 'none', fontSize: 18,
            color: COLORS.primary, cursor: 'pointer', padding: '2px 6px',
          }}>←</button>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: COLORS.text }}>
              {cfg.icon} Amend {cfg.label}
            </div>
            <div style={{ fontSize: 12, color: COLORS.textMuted }}>
              {step === 'row'    && 'Select the entry to amend'}
              {step === 'field'  && 'Select the field to correct'}
              {step === 'reason' && 'Enter correction and reason'}
            </div>
          </div>
        </div>

        {/* HIPAA notice */}
        <div style={{
          background: COLORS.warningLight, border: `1px solid ${COLORS.warningAlpha}`,
          borderRadius: 8, padding: '8px 12px', marginBottom: 16,
          fontSize: 12, color: COLORS.warning, fontWeight: 600,
        }}>
          ⚠️ Amendments are permanently recorded in the audit trail.
        </div>

        {/* ── STEP: Row selection ── */}
        {step === 'row' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {rows.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 24, color: COLORS.textMuted }}>
                No entries found for this section.
              </div>
            ) : (
              rows.map((row, i) => (
                <button key={row[cfg.pkField] ?? i}
                  onClick={() => { setSelectedRow(row); setStep('field'); }}
                  style={{
                    width: '100%', padding: '12px 16px',
                    background: COLORS.bg, border: `1px solid ${COLORS.border}`,
                    borderRadius: 10, cursor: 'pointer', textAlign: 'left',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  }}
                >
                  <span style={{ fontSize: 14, fontWeight: 600, color: COLORS.text }}>
                    {cfg.rowLabel(row)}
                  </span>
                  <span style={{ fontSize: 16, color: COLORS.primary }}>›</span>
                </button>
              ))
            )}
          </div>
        )}

        {/* ── STEP: Field selection ── */}
        {step === 'field' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {allowedFields.map(field => {
              const current = getDisplayValue(selectedRow, field);
              return (
                <button key={field.key}
                  onClick={() => handleFieldSelect(field)}
                  style={{
                    width: '100%', padding: '12px 16px',
                    background: COLORS.bg, border: `1px solid ${COLORS.border}`,
                    borderRadius: 10, cursor: 'pointer', textAlign: 'left',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  }}
                >
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.text }}>
                      {field.label}
                    </div>
                    {current && (
                      <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 2 }}>
                        Current: {current}
                      </div>
                    )}
                  </div>
                  <span style={{ fontSize: 16, color: COLORS.primary }}>›</span>
                </button>
              );
            })}
          </div>
        )}

        {/* ── STEP: Value + reason ── */}
        {step === 'reason' && fieldDef && (
          <div>
            <div style={{
              background: COLORS.bg, border: `1px solid ${COLORS.border}`,
              borderRadius: 8, padding: '10px 14px', marginBottom: 16,
            }}>
              <div style={{ fontSize: 11, color: COLORS.textMuted, fontWeight: 600, marginBottom: 4 }}>
                AMENDING
              </div>
              <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.text }}>
                {cfg.rowLabel(selectedRow)} → {fieldDef.label}
              </div>
            </div>

            {/* Old value (read-only) */}
            <div style={{ marginBottom: 14 }}>
              <label htmlFor="amend-old-value" style={{ fontSize: 12, fontWeight: 600, color: COLORS.textMuted, display: 'block', marginBottom: 4 }}>
                Current Value
              </label>
              <div id="amend-old-value" style={{
                padding: '8px 12px', borderRadius: 6, fontSize: 14,
                border: `1px solid ${COLORS.border}`, color: COLORS.textMuted,
                background: COLORS.bg, textDecoration: 'line-through',
              }}>
                {oldValue || '(empty)'}
              </div>
            </div>

            {/* New value */}
            <div style={{ marginBottom: 14 }}>
              <label htmlFor="amend-new-value" style={{ fontSize: 12, fontWeight: 600, color: COLORS.textMuted, display: 'block', marginBottom: 4 }}>
                Corrected Value <span style={{ color: COLORS.danger }}>*</span>
              </label>

              {fieldDef.type === 'boolean' ? (
                <div id="amend-new-value" style={{ display: 'flex', gap: 8 }}>
                  {['Yes', 'No'].map(optLabel => (
                    <button key={optLabel} onClick={() => setNewValue(optLabel === 'Yes' ? '1' : '0')}
                      style={{
                        flex: 1, padding: '10px 0', borderRadius: 8,
                        border: `1px solid ${newValue === (optLabel === 'Yes' ? '1' : '0') ? COLORS.primary : COLORS.border}`,
                        background: newValue === (optLabel === 'Yes' ? '1' : '0') ? COLORS.primaryLight : COLORS.surface,
                        color: newValue === (optLabel === 'Yes' ? '1' : '0') ? COLORS.primary : COLORS.textMuted,
                        fontSize: 14, fontWeight: 600, cursor: 'pointer',
                      }}>
                      {optLabel}
                    </button>
                  ))}
                </div>
              ) : fieldDef.type === 'select' ? (
                <select id="amend-new-value" value={newValue} onChange={e => setNewValue(e.target.value)} style={{
                  width: '100%', padding: '10px 12px', borderRadius: 6,
                  border: `1px solid ${COLORS.border}`, fontSize: 14,
                  color: COLORS.text, background: COLORS.surface, fontFamily: 'inherit',
                }}>
                  <option value="">Select…</option>
                  {fieldDef.options?.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              ) : fieldDef.type === 'number' ? (
                <input id="amend-new-value" type="number" value={newValue} onChange={e => setNewValue(e.target.value)}
                  style={{
                    width: '100%', padding: '10px 12px', borderRadius: 6,
                    border: `1px solid ${COLORS.border}`, fontSize: 14,
                    color: COLORS.text, background: COLORS.surface,
                    boxSizing: 'border-box', fontFamily: 'inherit',
                  }}
                />
              ) : (
                <input id="amend-new-value" type="text" value={newValue} onChange={e => setNewValue(e.target.value)}
                  style={{
                    width: '100%', padding: '10px 12px', borderRadius: 6,
                    border: `1px solid ${COLORS.border}`, fontSize: 14,
                    color: COLORS.text, background: COLORS.surface,
                    boxSizing: 'border-box', fontFamily: 'inherit',
                  }}
                />
              )}
            </div>

            {/* Reason */}
            <div style={{ marginBottom: 20 }}>
              <label htmlFor="amend-reason" style={{ fontSize: 12, fontWeight: 600, color: COLORS.textMuted, display: 'block', marginBottom: 4 }}>
                Reason for Amendment <span style={{ color: COLORS.danger }}>*</span>
              </label>
              <textarea
                id="amend-reason"
                value={reason}
                onChange={e => setReason(e.target.value)}
                placeholder="Explain why this correction is needed…"
                rows={3}
                style={{
                  width: '100%', padding: '10px 12px', borderRadius: 6,
                  border: `1px solid ${reason.trim() ? COLORS.border : COLORS.dangerAlpha}`,
                  fontSize: 14, color: COLORS.text, resize: 'none',
                  boxSizing: 'border-box', fontFamily: 'inherit',
                }}
              />
              {!reason.trim() && (
                <div style={{ fontSize: 11, color: COLORS.danger, marginTop: 4 }}>
                  A reason is required for all amendments.
                </div>
              )}
            </div>

            {error && (
              <div style={{
                background: COLORS.dangerLight, border: `1px solid ${COLORS.dangerAlpha}`,
                borderRadius: 8, padding: '10px 14px', marginBottom: 16,
                fontSize: 13, color: COLORS.danger, fontWeight: 600,
              }}>
                ⚠️ {error}
              </div>
            )}

            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={handleBack} disabled={amending} style={{
                flex: 1, padding: '12px 0',
                background: COLORS.bg, border: `1px solid ${COLORS.border}`,
                borderRadius: 10, fontSize: 14, fontWeight: 600,
                color: COLORS.textMuted, cursor: 'pointer',
              }}>
                Back
              </button>
              <button
                onClick={handleSubmit}
                disabled={amending || !reason.trim() || !newValue}
                style={{
                  flex: 2, padding: '12px 0',
                  background: amending || !reason.trim() || !newValue
                    ? COLORS.border : COLORS.warning,
                  border: 'none', borderRadius: 10,
                  fontSize: 14, fontWeight: 700,
                  color: amending || !reason.trim() || !newValue ? COLORS.textMuted : '#fff',
                  cursor: amending || !reason.trim() || !newValue ? 'not-allowed' : 'pointer',
                  transition: 'background 0.2s',
                }}
              >
                {amending ? 'Saving…' : 'Submit Amendment ✏️'}
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}